import { WORK_ORDERS_CRUD_ACTION_CREATORS } from '../actions/work_orders_actions';
import {
	morePipelineFiltersBuyer,
	pipelineFiltersBuyer,
	morePipelineFiltersSupplier,
	pipelineFiltersSupplier,
} from '../components/WorkOrderTable/config/pipelineFilters/pipelineFilters';
import {
	DEFAULT_SORTER,
	GLOBAL_DEBOUNCE_TIME,
	ROLE_TYPES,
	FETCH_DATA_PREFIX_FOR_TARGET_COLLECTION,
	CONTRACTOR_PROPS,
} from '../utils/DataConstants';
import {
	downloadWorkOrderCSVForBuyer,
	downloadWorkOrderCSVForSupplier,
	getCountByDisplayStatusForBuyer,
	getCountByDisplayStatusForSupplier,
	getCountByStatusForBuyer,
	getCountByStatusForSupplier,
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from './work_orders_thunks';
import { nullSafeGet, flattenArrayValues } from '../utils/DataAccessUtils';
import { getBuyerOrContractorOrClientDetailsFromSearch } from '../utils/WorkOrderUtils';
import { collapseHighlightAnalyzerVariants } from '../utils/SearchHighlightUtils';
const queryString = require('qs');
const debounce = require('debounce-promise');

export const workOrderSearchAndAPIAdaptor = (workOrders) =>
	workOrders.map(({ source: workOrder, highlight, jsonHighlight, score }) => {
		if (highlight) {
			highlight = collapseHighlightAnalyzerVariants(highlight);
			// The backend returns internal-note highlights under `internalNotes`, but
			// the UI reads them under `internalSpaces`. Alias here so all consumers
			// see a consistent key without having to know the backend name.
			if (highlight.internalNotes && !highlight.internalSpaces) {
				highlight.internalSpaces = highlight.internalNotes;
				delete highlight.internalNotes;
			}
		}
		if (jsonHighlight && jsonHighlight.length > 0) {
			highlight.troubleshooting = jsonHighlight;
			if (highlight['troubleshootingQuestionAndAnswers.answer']) {
				const answerMap = new Map();
				highlight['troubleshootingQuestionAndAnswers.answer'].forEach((highlighted) => {
					// Remove  tags
					const plainText = highlighted.replace(/<\/?em>/g, '');
					answerMap.set(plainText, highlighted);
				});
				highlight.troubleshooting.forEach((item) => {
					answerMap.forEach((highlighted, plainText) => {
						if (item.answer.includes(plainText)) {
							item.answer = item.answer.replace(new RegExp(`\\b${plainText}\\b`, 'g'), highlighted);
						}
					});
				});
			}
		}
		const result = {
			...workOrder,
			id: parseInt(workOrder.id),
			highlight,
			searchRecord: true,
			score,
		};

		if (workOrder.locationName) {
			if (workOrder.locationId === -100) {
				result[CONTRACTOR_PROPS.TO_WO] = {
					contractorLocation: {
						name: workOrder.locationName,
						timeZoneId: workOrder.locationTimeZoneId,
						storeId: workOrder.locationStoreId,
					},
				};
			} else {
				result.location = {
					name: workOrder.locationName,
					timeZoneId: workOrder.locationTimeZoneId,
					id: workOrder.locationId,
					storeId: workOrder.locationStoreId,
				};
			}
		}

		if (workOrder.areaHierarchyName) {
			result.area = {
				id: workOrder.areaId,
				hierarchyName: workOrder.areaHierarchyName,
			};
		}

		if (workOrder.descriptionInHtml) {
			result.description = workOrder.descriptionInHtml;
		}

		if (workOrder.spendCategoryName) {
			result.spendCategory = {
				name: workOrder.spendCategoryName,
			};
		}

		if (workOrder.spendCategoryName) {
			result.spendCategory = {
				name: workOrder.spendCategoryName,
			};
		}

		if (workOrder.assetName || workOrder.assetNumber) {
			result.asset = {};
			if (workOrder.assetName) {
				result.asset.name = workOrder.assetName;
			}
			if (workOrder.assetNumber) {
				result.asset.assetNumber = workOrder.assetNumber;
			}
			if (workOrder.assetId) {
				result.asset.id = workOrder.assetId;
			}
			if (workOrder.assetTypeId) {
				result.asset.assetType = { id: workOrder.assetTypeId, name: workOrder.assetTypeName };
			}
		}

		if (workOrder.problemTypeName) {
			if (workOrder.locationId === -100) {
				result[CONTRACTOR_PROPS.TO_WO] = {
					...(result[CONTRACTOR_PROPS.TO_WO] || {}),
					contractorProblemType: {
						name: workOrder.problemTypeName,
						hierarchyName: workOrder.problemTypeHierarchyName,
					},
				};
			} else {
				result.problemType = {
					name: workOrder.problemTypeName,
					hierarchyName: workOrder.problemTypeHierarchyName,
				};
			}
		}

		if (workOrder.supplierFacilityDisplayName) {
			result.supplierFacility = {
				displayName: workOrder.supplierFacilityDisplayName,
				status: workOrder.isInternalTeamAssignedSupplier ? 'private' : 'active',
			};
		}

		if (workOrder.buyerProjectIds) {
			result.buyerProjectIds = workOrder.buyerProjectIds;
		}
		if (workOrder.buyerProjectTitles) {
			result.buyerProjects = workOrder.buyerProjectTitles.map((title, index) => {
				return {
					id: workOrder.buyerProjectIds[index],
					title,
				};
			});
		}
		if (
			workOrder.lastServiceCallCheckInTime ||
			workOrder.lastServiceCallCheckOutTime ||
			workOrder.lastServiceCallServiceScheduledAt
		) {
			result.lastServiceCall = {
				checkInTime: workOrder.lastServiceCallCheckInTime,
				checkOutTime: workOrder.lastServiceCallCheckOutTime,
				serviceScheduledAt: workOrder.lastServiceCallServiceScheduledAt,
				leadTechnicianEmail: workOrder.lastServiceCallLeadTechnicianEmail,
			};
		}

		if (Array.isArray(workOrder.serviceCallOsDocs)) {
			result.associatedServiceCalls = workOrder.serviceCallOsDocs.map((sc) => ({
				serviceScheduledAt: sc.serviceScheduledAt,
				checkInTime: sc.checkinTime,
				checkOutTime: sc.checkoutTime,
				leadTechnicianEmail: sc.leadTechnicianEmail,
			}));
		}

		if (workOrder.woPriorityName) {
			if (workOrder.locationId === -100) {
				result[CONTRACTOR_PROPS.TO_WO] = {
					...(result[CONTRACTOR_PROPS.TO_WO] || {}),
					contractorWorkOrderPriority: {
						name: workOrder.woPriorityName,
						id: workOrder.woPriorityId,
						linkedToId: workOrder.woPriorityLinkedToId,
					},
				};
			} else {
				result.woPriority = {
					name: workOrder.woPriorityName,
					id: workOrder.woPriorityId,
					linkedToId: workOrder.woPriorityLinkedToId,
				};
			}
		}
		if (Boolean(workOrder.isSubContracted)) {
			result.subContractedToWorkOrder = {
				...(result[CONTRACTOR_PROPS.TO_WO] || {}),
				contractorStatus: workOrder.subContractedStatus,
				contractorDisplayStatus: workOrder.subContractedDisplayStatus,
				subSupplierFacilityId: nullSafeGet(
					'subContractedDetails.0.subContractedToSupplierFacilityId',
					workOrder
				),
			};
		}

		const buyerOrContractorDetail = getBuyerOrContractorOrClientDetailsFromSearch(workOrder);

		if (buyerOrContractorDetail) {
			result[buyerOrContractorDetail.key] = {
				...(result[buyerOrContractorDetail.key] || {}),
				...buyerOrContractorDetail.value,
			};
		}

		return result;
	});

const getMethodByRole = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? workOrdersRestCrudThunksForSupplier
		: workOrdersRestCrudThunksForBuyer;
export const parseURL = (
	handleQueryParams,
	history,
	location,
	preAppliedFilters,
	initialPagination,
	initialSorting,
	initialFilters = {}
) => {
	const defaultPageSize = 50;
	const defaultPagination = { current: 1, pageSize: defaultPageSize };
	let pagination = initialPagination || defaultPagination;
	let sorting = initialSorting || DEFAULT_SORTER;
	let filters = { ...preAppliedFilters, ...initialFilters };
	if (handleQueryParams && location) {
		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}
		const queryParams = queryString.parse(searchString);

		const {
			current = pagination.current,
			pageSize = pagination.pageSize,
			sort_by = sorting.sort_by,
			order = sorting.order,
			...workOrderFilters
		} = queryParams;

		pagination = { current: parseInt(current), pageSize: parseInt(pageSize) };
		sorting = { sort_by, order };
		filters = {
			...(initialFilters || {}),
			...(workOrderFilters || {}),
			...preAppliedFilters,
		};
	}
	return { pagination, sorting, filters };
};
export const fetchWorkOrders =
	(userType) =>
	(
		handleQueryParams,
		history,
		location,
		targetCollectionName,
		preAppliedFilters = {},
		initialPagination,
		initialSorting,
		initialFilters,
		ignoreRefreshCounts,
		addToTargetCollection = false
	) =>
	(dispatch, getState) => {
		const { pagination, sorting, filters } = parseURL(
			handleQueryParams,
			history,
			location,
			preAppliedFilters,
			initialPagination,
			initialSorting,
			initialFilters
		);
		dispatch(
			fetchData(userType)(
				handleQueryParams,
				history,
				null,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				ignoreRefreshCounts,
				addToTargetCollection
			)
		);
	};
export const updatePaginationWorkOrders =
	(userType) =>
	(handleQueryParams, history, targetCollectionName, pagination, ignoreRefreshCounts) =>
	(dispatch, getState) => {
		const { sorting, filters } = getState().work_orders[targetCollectionName];
		return dispatch(
			fetchData(userType)(
				handleQueryParams,
				history,
				null,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				ignoreRefreshCounts
			)
		);
	};

export const updateFiltersWorkOrders =
	(userType) =>
	(handleQueryParams, history, targetCollectionName, filters, ignoreRefreshCounts) =>
	(dispatch, getState) => {
		const {
			sorting,
			filters: storeFilters,
			pagination,
		} = getState().work_orders[targetCollectionName];
		const newFilters = { ...storeFilters, ...filters };
		let newSorting = { ...sorting };
		if (newFilters.search !== storeFilters.search) {
			const searchWords = (newFilters.search || '').trim().split(/\s+/);
			if (searchWords.length > 1 && sorting.sort_by === 'createdAt' && sorting.order === 'desc') {
				newSorting = { ...sorting, sort_by: '_score', order: 'desc' };
			} else if (
				searchWords.length < 2 &&
				sorting.sort_by === '_score' &&
				sorting.order === 'desc'
			) {
				newSorting = { ...sorting, sort_by: 'createdAt', order: 'desc' };
			}
		}
		dispatch(
			fetchData(userType)(
				handleQueryParams,
				history,
				null,
				targetCollectionName,
				{ ...pagination, current: 1 },
				newSorting,
				{
					...storeFilters,
					...filters,
				},
				ignoreRefreshCounts
			)
		);
	};

export const replaceFiltersAndSortingWorkOrders =
	(userType) =>
	(handleQueryParams, history, targetCollectionName, filters, sorting, ignoreRefreshCounts) =>
	(dispatch, getState) => {
		const { pagination } = getState().work_orders[targetCollectionName];
		dispatch(
			fetchData(userType)(
				handleQueryParams,
				history,
				null,
				targetCollectionName,
				{ ...pagination, current: 1 },
				sorting,
				filters,
				ignoreRefreshCounts
			)
		);
	};

export const clearFiltersWorkOrders =
	(userType) =>
	(handleQueryParams, history, targetCollectionName, preAppliedFilters, ignoreRefreshCounts) =>
	(dispatch, getState) => {
		const { pagination } = getState().work_orders[targetCollectionName];
		dispatch(
			fetchData(userType)(
				handleQueryParams,
				history,
				null,
				targetCollectionName,
				{ ...pagination, current: 1 },
				{ ...DEFAULT_SORTER },
				{
					...preAppliedFilters,
				},
				ignoreRefreshCounts
			)
		);
	};

export const updateSortingWorkOrders =
	(userType) =>
	(handleQueryParams, history, targetCollectionName, sorting, ignoreRefreshCounts) =>
	(dispatch, getState) => {
		const { filters, pagination } = getState().work_orders[targetCollectionName];
		dispatch(
			fetchData(userType)(
				handleQueryParams,
				history,
				null,
				targetCollectionName,
				{ ...pagination, current: 1 },
				sorting,
				filters,
				ignoreRefreshCounts
			)
		);
	};
export const downloadWorkOrderCSV = (userType) => (params, filters) => {
	const downloadFn =
		userType === ROLE_TYPES.SUPPLIER
			? downloadWorkOrderCSVForSupplier
			: downloadWorkOrderCSVForBuyer;
	// The Tracked view's list carries reminderStatusForCurrentUser=none only to split its rows into
	// reminder groups on screen; an export of that view covers every tracked work order.
	return downloadFn(params, { ...filters, reminderStatusForCurrentUser: undefined });
};
export const refreshCounts =
	(userType) => (targetCollectionName, listFilters) => (dispatch, getState) => {
		// The Tracked view lists its reminder groups separately, but its status chips count the
		// whole view, so the reminder grouping never narrows the counts.
		const filters = { ...listFilters, reminderStatusForCurrentUser: undefined };
		const finalPipelineFilters =
			userType === ROLE_TYPES.SUPPLIER ? pipelineFiltersSupplier(true) : pipelineFiltersBuyer(true);
		const finalMorePipelineFilters =
			userType === ROLE_TYPES.SUPPLIER
				? morePipelineFiltersSupplier(true)
				: morePipelineFiltersBuyer(true);

		const countByDisplayStatusMethod =
			userType === ROLE_TYPES.SUPPLIER
				? getCountByDisplayStatusForSupplier
				: getCountByDisplayStatusForBuyer;

		countByDisplayStatusMethod(
			flattenArrayValues({
				...filters,
				status: undefined,
				displayStatuses: finalPipelineFilters.map((_) => _.filter.displayStatus).join(','),
				statuses: undefined,
			})
		).then((res: any = []) => {
			finalPipelineFilters.map((filter) => {
				const category = res.find((_) => _.categoryName === filter.name);

				dispatch(
					WORK_ORDERS_CRUD_ACTION_CREATORS.countBySuccess(
						category ? category.count : 0,
						filter.name,
						targetCollectionName
					)
				);
			});
		});

		const countByStatusMethod =
			userType === ROLE_TYPES.SUPPLIER ? getCountByStatusForSupplier : getCountByStatusForBuyer;

		const getSubFilterStatuses = () => {
			const allDisplayStatuses = (filters.displayStatuses || filters.displayStatus || '').split(
				','
			);
			return allDisplayStatuses
				.reduce((acc, dps) => {
					return [
						...acc,
						...(dps !== undefined && finalMorePipelineFilters[dps] !== undefined
							? finalMorePipelineFilters[dps]
							: []),
					];
				}, [])
				.map((_) => _.filter.status);
		};

		const subStatuses = getSubFilterStatuses();
		if (filters.displayStatuses) {
			countByStatusMethod(
				flattenArrayValues({
					...filters,
					status: undefined,
					statuses: subStatuses,
					displayStatus: undefined,
					displayStatuses: undefined,
				})
			).then((res: any = []) => {
				subStatuses.map((filter) => {
					const category = res.find((_) => _.categoryName === filter);

					dispatch(
						WORK_ORDERS_CRUD_ACTION_CREATORS.countBySuccess(
							category ? category.count : 0,
							filter,
							targetCollectionName
						)
					);
				});
			});
		}
	};

const fetchDataFromServer = (props) => {
	const {
		dispatch,
		userType,
		addtionalParams,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		ignoreRefreshCounts,
		addToTargetCollection = false,
	} = props;
	!ignoreRefreshCounts && dispatch(refreshCounts(userType)(targetCollectionName, filters));

	const body = flattenArrayValues({ ...addtionalParams, ...filters });
	return dispatch(
		getMethodByRole(userType).searchWithPostMethod(
			body,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection,
			workOrderSearchAndAPIAdaptor
		)
	);
};

// Map to store debounced functions per targetCollectionName
// This ensures each collection has its own debounce queue
const debouncedFetchDataMap = new Map();

const getDebouncedFetchData = (targetCollectionName) => {
	if (!targetCollectionName) {
		if (!debouncedFetchDataMap.has('_default')) {
			debouncedFetchDataMap.set('_default', debounce(fetchDataFromServer, GLOBAL_DEBOUNCE_TIME));
		}
		return debouncedFetchDataMap.get('_default');
	}

	if (!debouncedFetchDataMap.has(targetCollectionName)) {
		debouncedFetchDataMap.set(
			targetCollectionName,
			debounce(fetchDataFromServer, GLOBAL_DEBOUNCE_TIME)
		);
	}
	return debouncedFetchDataMap.get(targetCollectionName);
};

const fetchData =
	(userType) =>
	(
		handleQueryParams,
		history,
		addtionalParams,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		ignoreRefreshCounts,
		addToTargetCollection = false
	) =>
	(dispatch) => {
		const props = {
			dispatch,
			userType,
			addtionalParams,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			ignoreRefreshCounts,
			addToTargetCollection,
		};
		if (handleQueryParams) {
			const queryParams = queryString.stringify({
				...{
					current: pagination.current,
					pageSize: pagination.pageSize,
				},
				...sorting,
				...filters,
				...addtionalParams,
			});
			history.replace(`${window.location.pathname}?${queryParams}`);
		}
		if (
			targetCollectionName &&
			targetCollectionName.includes(FETCH_DATA_PREFIX_FOR_TARGET_COLLECTION)
		) {
			return fetchDataFromServer(props);
		} else {
			dispatch(WORK_ORDERS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName));
			dispatch(WORK_ORDERS_CRUD_ACTION_CREATORS.updatePagination(pagination, targetCollectionName));
			const debouncedFetchData = getDebouncedFetchData(targetCollectionName);
			return debouncedFetchData(props);
		}
	};
